//#region IMPORTS
import { STICKY_TOP_TABLE_CLASS } from "../_layout/stickyOffsets";
import { useTranslation } from "react-i18next";
import { MetaData } from "../../models/pagination";
import PaginationBarItem from "./PaginationBarItem";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  onPageChange: (page: number) => void;
  metaData?: MetaData;
  pageType: string;
  loaded?: boolean;
  hide?: boolean;
}
//#endregion

// Server-side pagination bar. Rendered ONCE, above the table (see MainTable),
// as part of the sticky stack right under the filter bar (see
// _layout/stickyOffsets.ts) — so paging never needs a scroll, and the old copy
// at the bottom became pointless. Tailwind only; text-sm to match the tables
// (rows are text-xs), active page text-lg. Brand colours as arbitrary values
// like elsewhere in the app: #254559 = --blauw, #3e5902 = --groen,
// #f2f2f2 = --grijs_light (App.css).
export default function PaginationBar({
  onPageChange,
  metaData,
  pageType,
  loaded = true,
  hide = false,
}: Props) {
  // Translation
  const { t } = useTranslation();

  // Nothing to page through → no bar at all (the client-side twin does the
  // same via totalAmmount <= pageSize).
  if (hide || !metaData || metaData.last_page <= 1) return null;
  const meta: MetaData = metaData;

  // Sticky, white, below the loading overlay (z-20) but above the rows
  // scrolling underneath. Flush against the filter bar; PADDING (not margin)
  // towards the column header, so rows never show through a gap.
  return (
    <header className={classNames("sticky z-10 bg-white", STICKY_TOP_TABLE_CLASS)}>
      <div className="flex justify-between text-sm text-[#254559] pt-1 pb-3">
      <div className="flex flex-col justify-center items-start">
        {meta.last_page > 1 && (
          <div className="flex justify-center items-center">
            <p
              className={classNames(
                "text-left transition-all duration-300 ease-in-out",
                loaded ? "text-[#254559]" : "text-[#f2f2f2]"
              )}
            >
              {meta.current_page}
            </p>
            <div className="w-0.5 h-4 mx-3 bg-[#f2f2f2]" />
            <p className="text-xs text-left">{meta.last_page}</p>
          </div>
        )}
      </div>

      {/* Page links */}
      <div className="flex flex-row justify-center items-center">
        {meta.links.map((link, index) => (
          <PaginationBarItem
            currentPage={meta.current_page}
            lastPage={meta.last_page}
            link={link}
            key={index}
            onPageChange={(pageNr) => onPageChange(pageNr)}
          />
        ))}
      </div>

      <p className="text-sm">
        <b>
          {meta.from} - {meta.to}
        </b>{" "}
        {t("rituals.of")}{" "}
        <b>
          {meta.total} {pageType}
        </b>
      </p>
      </div>
    </header>
  );
}
